import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, BarChart, Bar,
} from 'recharts';
import {
  TrendingUp, TrendingDown, Minus, RefreshCw, CalendarDays,
  ArrowUpRight, ArrowDownRight, AlertTriangle, Lightbulb,
  Activity, Target, Zap, BarChart3,
} from 'lucide-react';
import { ExportDropdown } from '@/components/ExportDropdown';
import { downloadCSV, buildPDF } from '@/lib/exportUtils';
import { FilterPanel } from '@/components/FilterPanel';
import { HelpBubble } from '@/components/HelpBubble';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AIPanel } from '@/components/AIPanel';
import { TrustScoreBadge } from '@/components/TrustScoreBadge';
import { TrustScoreGate } from '@/components/TrustScoreGate';
import { toast } from 'sonner';
import { useCurrency } from '@/lib/useCurrency';
import { useGlobalFilters } from '@/lib/FilterContext';

const TREND_ICONS = {
  upward: TrendingUp,
  increasing: TrendingUp,
  downward: TrendingDown,
  decreasing: TrendingDown,
  stable: Minus,
  volatile: Activity,
};

const TREND_COLORS = {
  upward: 'text-amber-500',
  increasing: 'text-amber-500',
  downward: 'text-emerald-500',
  decreasing: 'text-emerald-500',
  stable: 'text-slate-500',
  volatile: 'text-rose-500',
};

const formatCurrency = (v, cs = '') => {
  if (v >= 1000000) return `${cs}${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${cs}${(v / 1000).toFixed(0)}K`;
  return `${cs}${v.toFixed(0)}`;
};

const formatMonth = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(mo) - 1] || mo} ${y?.slice(2)}`;
};

export default function SpendForecasting() {
  const { symbol: cs } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [horizon, setHorizon] = useState('6');
  const { filters, setFilters } = useGlobalFilters();

  const VISIBLE_FILTERS = [
    'category', 'category_l2', 'region', 'country', 'sbu', 'department', 'fiscal_year', 'buyer',
    'cost_center', 'business_unit',
  ];

  const fetchForecast = useCallback(async (h, f) => {
    setLoading(true);
    setError(null);
    try {
      const params = { horizon: h || horizon };
      const activeFilters = f || filters;
      Object.entries(activeFilters).forEach(([k, v]) => {
        if (v && v !== 'all') params[k] = v;
      });
      const res = await api.get('/forecasting/analysis', { params });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate forecast');
    } finally {
      setLoading(false);
    }
  }, [horizon, filters]);

  useEffect(() => { fetchForecast(horizon, filters); }, [fetchForecast]);

  const handleHorizonChange = (val) => {
    setHorizon(val);
    fetchForecast(val, filters);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    fetchForecast(horizon, newFilters);
  };

  // ── Chart data ──
  const chartData = [];
  if (data) {
    (data.historical || []).forEach(m => {
      chartData.push({
        month: formatMonth(m.month),
        rawMonth: m.month,
        Historical: Math.round(m.total),
        type: 'historical',
      });
    });
    const lastHist = data.historical?.[data.historical.length - 1];
    if (lastHist && data.forecast?.length) {
      // Bridge point: last historical month also starts forecast line
      chartData[chartData.length - 1].Forecast = Math.round(lastHist.total);
    }
    (data.forecast || []).forEach(m => {
      chartData.push({
        month: formatMonth(m.month),
        rawMonth: m.month,
        Forecast: Math.round(m.total),
        type: 'forecast',
      });
    });
  }

  // ── Category forecast data ──
  const categoryData = (data?.insights?.category_trends || []).map(ct => {
    const TIcon = TREND_ICONS[ct.trend] || Minus;
    const tColor = TREND_COLORS[ct.trend] || 'text-slate-500';
    return { ...ct, TIcon, tColor };
  });

  // ── Category bar chart ──
  const catBarData = (data?.insights?.category_trends || []).map(ct => ({
    name: ct.category?.length > 15 ? ct.category.slice(0, 13) + '...' : ct.category,
    fullName: ct.category,
    'Change %': ct.forecast_change_pct || 0,
  }));

  const tooltipStyle = {
    backgroundColor: 'hsl(var(--popover))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '8px',
  };

  // ── Exports ──
  const exportCSV = () => {
    if (!data) return;
    const rows = [['Month', 'Type', 'Amount']];
    (data.historical || []).forEach(m => rows.push([m.month, 'Historical', m.total]));
    (data.forecast || []).forEach(m => rows.push([m.month, 'Forecast', m.total]));
    downloadCSV(rows, 'spend_forecast');
  };
  const exportPDF = () => {
    if (!data) return;
    const allMonths = [...(data.historical || []).map(m => ({...m, type: 'Historical'})), ...(data.forecast || []).map(m => ({...m, type: 'Forecast'}))];
    buildPDF([{
      title: `Spend Forecast (${horizon}-month horizon)`,
      headers: ['Month', 'Type', 'Amount'],
      rows: allMonths.map(m => [m.month, m.type, `${cs}${m.total.toLocaleString()}`]),
    }], 'Spend_Forecast');
  };

  // ── Error state ──
  if (!loading && error) {
    return (
      <div className="space-y-8" data-testid="forecasting-page">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Spend Forecasting</h1>
          <p className="text-sm text-muted-foreground mt-1">AI-powered spend trend prediction</p>
        </div>
        <Card className="py-12">
          <CardContent className="text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-destructive opacity-60" />
            <p className="text-lg font-medium mb-2">Forecast Unavailable</p>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => fetchForecast(horizon, filters)} data-testid="retry-button">Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const summary = data?.summary || {};
  const insights = data?.insights || {};
  const trend = insights.trend || 'stable';
  const TrendIcon = TREND_ICONS[trend] || Minus;
  const trendColor = TREND_COLORS[trend] || 'text-slate-500';

  return (
    <TrustScoreGate mode="hard" moduleLabel="Spend Forecasting">
    <div className="space-y-6" data-testid="forecasting-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Spend Forecasting</h1>
          <p className="text-sm text-muted-foreground mt-1">
            AI-powered spend prediction based on {data?.data_months || '...'} months of historical data
            {data?.filters_applied?.length > 0 && <span className="text-primary"> (filtered)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TrustScoreBadge pageName="forecasting" />
          <Select value={horizon} onValueChange={handleHorizonChange}>
            <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="horizon-selector">
              <CalendarDays className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3-month forecast</SelectItem>
              <SelectItem value="6">6-month forecast</SelectItem>
              <SelectItem value="12">12-month forecast</SelectItem>
            </SelectContent>
          </Select>
          <ExportDropdown onExportCSV={exportCSV} onExportPDF={exportPDF} />
          <Button variant="outline" size="sm" className="h-8" onClick={() => fetchForecast(horizon, filters)} disabled={loading} data-testid="refresh-forecast">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      <FilterPanel
        filters={filters}
        onFiltersChange={handleFiltersChange}
        visibleFilters={VISIBLE_FILTERS}
        compact={true}
        pageName="forecasting"
      />

      {/* Filter context badge */}
      {data?.filters_applied?.length > 0 && !loading && (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs" data-testid="forecast-filter-context">
            Forecast filtered by: {data.filter_context}
          </Badge>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center h-48 gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <p className="text-sm text-muted-foreground">Generating AI-powered forecast...</p>
        </div>
      )}

      {!loading && data && (
        <>
          {/* ── KPI Cards ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Card data-testid="kpi-trend">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">Trend <HelpBubble text="Direction of the fitted trend line across historical monthly spend. Up = growing spend base, Down = shrinking. Doesn't account for seasonality — see chart for that." /></CardTitle>
                <TrendIcon className={cn('w-5 h-5', trendColor)} />
              </CardHeader>
              <CardContent>
                <div className={cn('text-lg font-bold capitalize', trendColor)}>{trend}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {insights.monthly_growth_pct != null
                    ? `${insights.monthly_growth_pct > 0 ? '+' : ''}${insights.monthly_growth_pct}% monthly`
                    : 'Based on historical data'}
                </p>
              </CardContent>
            </Card>

            <Card data-testid="kpi-projected-total">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">Projected ({horizon}mo) <HelpBubble text="Total projected spend across the horizon window. Uses a Holt-Winters style forecast with 12-month seasonality when enough history exists; falls back to trend-only otherwise." /></CardTitle>
                <Target className="w-5 h-5 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold">{formatCurrency(summary.projected_period_total || 0, cs)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary.growth_vs_historical_pct != null && (
                    <span className={summary.growth_vs_historical_pct > 0 ? 'text-amber-500' : 'text-emerald-500'}>
                      {summary.growth_vs_historical_pct > 0 ? '+' : ''}{summary.growth_vs_historical_pct}% vs historical
                    </span>
                  )}
                </p>
              </CardContent>
            </Card>

            <Card data-testid="kpi-avg-monthly">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">Avg Monthly (forecast) <HelpBubble text="Mean projected monthly spend across the horizon. Useful for budget-planning conversations with Finance." /></CardTitle>
                <BarChart3 className="w-5 h-5 text-indigo-500" />
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold">{formatCurrency(summary.avg_monthly_forecast || 0, cs)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Historical avg: {formatCurrency(data.historical_avg_monthly || 0, cs)}
                </p>
              </CardContent>
            </Card>

            <Card data-testid="kpi-peak">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">Peak Month <HelpBubble text="Highest-spend month in the forecast window. Line this up with contract renewal dates and supplier capacity ahead of time." /></CardTitle>
                <Zap className="w-5 h-5 text-amber-500" />
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold">{formatMonth(summary.peak_month) || '-'}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary.highest_growth_category
                    ? `Led by ${summary.highest_growth_category}`
                    : 'Highest forecasted spend'}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* ── Main Forecast Chart ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card data-testid="forecast-chart">
              <CardHeader>
                <CardTitle>Spend Trend & {horizon}-Month Forecast</CardTitle>
                <CardDescription>Historical spend (solid) with AI-projected forecast (dashed)</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="histGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="foreGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="month"
                      stroke="hsl(var(--muted-foreground))"
                      tick={{ fontSize: 10 }}
                      interval={Math.max(0, Math.floor(chartData.length / 12))}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      tickFormatter={(v) => formatCurrency(v, cs)}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(val) => [formatCurrency(val, cs)]}
                      labelFormatter={(label) => label}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="Historical"
                      stroke="hsl(var(--primary))"
                      fill="url(#histGrad)"
                      strokeWidth={2}
                      dot={false}
                      connectNulls={false}
                    />
                    <Area
                      type="monotone"
                      dataKey="Forecast"
                      stroke="#f59e0b"
                      fill="url(#foreGrad)"
                      strokeWidth={2}
                      strokeDasharray="6 3"
                      dot={false}
                      connectNulls={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>

          {/* ── Insights + Category Trends (2 col) ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* AI Insights */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <Card className="h-full" data-testid="forecast-insights">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-500" /> AI Insights
                  </CardTitle>
                  <CardDescription>{insights.seasonality_note}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(insights.key_insights || []).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Key Findings</h4>
                      <ul className="space-y-2">
                        {insights.key_insights.map((insight, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <ArrowUpRight className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
                            <span>{insight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(insights.risk_factors || []).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Risk Factors</h4>
                      <ul className="space-y-2">
                        {insights.risk_factors.map((risk, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-500 shrink-0" />
                            <span>{risk}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Category Forecast Change Chart */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card className="h-full" data-testid="category-forecast-chart">
                <CardHeader>
                  <CardTitle>Category Forecast Change</CardTitle>
                  <CardDescription>Projected % change by spend category</CardDescription>
                </CardHeader>
                <CardContent>
                  {catBarData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={catBarData} layout="vertical" margin={{ left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis type="number" stroke="hsl(var(--muted-foreground))" tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
                        <YAxis dataKey="name" type="category" width={120} stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} />
                        <Tooltip
                          contentStyle={tooltipStyle}
                          formatter={(val, name, props) => [`${val}%`, props.payload.fullName]}
                        />
                        <Bar dataKey="Change %" radius={[0, 4, 4, 0]} barSize={18}>
                          {catBarData.map((entry, idx) => (
                            <React.Fragment key={idx}>
                              {/* Can't use Cell import in recharts easily, use fill on Bar */}
                            </React.Fragment>
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-10">No category trend data available</p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* ── Category Trends Table ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <Card data-testid="category-trends-table">
              <CardHeader>
                <CardTitle>Category Trend Details</CardTitle>
                <CardDescription>AI-projected trends for top spend categories</CardDescription>
              </CardHeader>
              <CardContent>
                {categoryData.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Category</th>
                          <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Trend</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Forecast Change</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categoryData.map(ct => (
                          <tr key={ct.category} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                            <td className="py-2 px-3 font-medium text-sm">{ct.category}</td>
                            <td className="py-2 px-3 text-center">
                              <Badge variant="outline" className={cn('text-xs capitalize', ct.tColor)}>
                                <ct.TIcon className="w-3 h-3 mr-1" />
                                {ct.trend}
                              </Badge>
                            </td>
                            <td className={cn('py-2 px-3 text-right font-mono text-sm font-semibold',
                              (ct.forecast_change_pct || 0) > 0 ? 'text-amber-500' : (ct.forecast_change_pct || 0) < 0 ? 'text-emerald-500' : 'text-muted-foreground'
                            )}>
                              {(ct.forecast_change_pct || 0) > 0 ? '+' : ''}{ct.forecast_change_pct || 0}%
                            </td>
                            <td className="py-2 px-3 text-sm text-muted-foreground max-w-sm">{ct.note}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-6">Category trend analysis will appear after forecast generation.</p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* ── Forecast Data Table ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card data-testid="forecast-data-table">
              <CardHeader>
                <CardTitle>Monthly Forecast Breakdown</CardTitle>
                <CardDescription>Projected monthly spend with category allocation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Month</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Projected Total</th>
                        {(data.top_categories || []).slice(0, 5).map(cat => (
                          <th key={cat} className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">{cat.length > 12 ? cat.slice(0, 10) + '..' : cat}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(data.forecast || []).map(m => (
                        <tr key={m.month} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                          <td className="py-2 px-3 font-medium text-sm">{formatMonth(m.month)}</td>
                          <td className="py-2 px-3 text-right font-mono text-sm font-semibold">{formatCurrency(m.total, cs)}</td>
                          {(data.top_categories || []).slice(0, 5).map(cat => (
                            <td key={cat} className="py-2 px-3 text-right font-mono text-xs text-muted-foreground">
                              {m.categories?.[cat] ? formatCurrency(m.categories[cat], cs) : '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </>
      )}
      <AIPanel page="forecasting" filters={filters} />
    </div>
    </TrustScoreGate>
  );
}
